import { cva } from "class-variance-authority";

export const tabListContainerCva = cva("relative flex bg-transparent", {
  variants: {
    orientation: {
      horizontal: "flex-row items-center w-full",
      vertical: "flex-col",
    },
    // The header bar has only the inkbar track as its line, so it drops the list's own borders.
    header: {
      true: null,
      false: null,
    },
    listPosition: {
      left: null,
      right: null,
    },
    tile: {
      true: null,
      false: null,
    },
  },
  compoundVariants: [
    {
      // The single 1px base line under the whole bar, also under the scroll buttons and the right slot.
      orientation: "horizontal",
      header: false,
      class: "border-b border-gray-100 dark:border-dark-700",
    },
    {
      orientation: "vertical",
      tile: false,
      class: "min-w-[200px]",
    },
    {
      orientation: "vertical",
      tile: true,
      class: "shrink-0 min-w-[100px] max-w-[200px]",
    },
  ],
  defaultVariants: {
    header: false,
  },
});

export const tabListPreviousButtonCva = cva("absolute flex items-center justify-center border-none bg-transparent cursor-pointer text-primary-600 dark:text-primary-400 z-10 p-2 hover:bg-gray-50 dark:hover:bg-dark-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-dark-850", {
  variants: {
    orientation: {
      horizontal: "top-0 h-full left-0 shadow-[4px_0_6px_-4px_rgba(0,0,0,0.1)] dark:shadow-[4px_0_6px_-4px_rgba(0,0,0,0.3)]",
      vertical: "w-full h-8 top-0 shadow-[0_4px_6px_-4px_rgba(0,0,0,0.1)] dark:shadow-[0_4px_6px_-4px_rgba(0,0,0,0.3)]",
    },
    listPosition: {
      left: null,
      right: null,
    },
  },
  compoundVariants: [
    {
      orientation: "vertical",
      listPosition: "left",
      class: "left-0",
    },
    {
      orientation: "vertical",
      listPosition: "right",
      class: "right-0",
    },
  ],
});

export const tabListNextButtonCva = cva("absolute flex items-center justify-center border-none bg-transparent cursor-pointer text-primary-600 dark:text-primary-400 z-10 p-2 hover:bg-gray-50 dark:hover:bg-dark-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white dark:disabled:hover:bg-dark-850", {
  variants: {
    orientation: {
      horizontal: "top-0 h-full right-0 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] dark:shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.3)]",
      vertical: "w-full h-8 bottom-0 shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.3)]",
    },
    listPosition: {
      left: null,
      right: null,
    },
  },
  compoundVariants: [
    {
      orientation: "vertical",
      listPosition: "left",
      class: "left-0",
    },
    {
      orientation: "vertical",
      listPosition: "right",
      class: "right-0",
    },
  ],
});

// The 2px track behind the inkbar, for vertical rails and the header bar. Other horizontal bars use the list's 1px
// bottom border as their line.
export const tabListInkbarBackgroundCva = cva("absolute bg-gray-100 dark:bg-dark-700", {
  variants: {
    orientation: {
      horizontal: "bottom-0 left-0 right-0 h-[2px]",
      vertical: "top-0 bottom-0 w-[2px]",
    },
    listPosition: {
      left: null,
      right: null,
    },
  },
  compoundVariants: [
    {
      orientation: "vertical",
      listPosition: "left",
      class: "right-0",
    },
    {
      orientation: "vertical",
      listPosition: "right",
      class: "left-0",
    },
  ],
});

export const tabListActiveInkbarCva = cva("absolute transition-all duration-200 z-10", {
  variants: {
    error: {
      true: "bg-red-600! dark:bg-red-400!",
      false: "bg-primary-600 dark:bg-primary-400",
    },
    orientation: {
      horizontal: null,
      vertical: null,
    },
    tile: {
      true: null,
      false: null,
    },
  },
  compoundVariants: [
    {
      orientation: "vertical",
      tile: true,
      class: "rounded-lg",
    },
  ],
});

// Horizontal: the wrapper fills the bar's height, so the inkbar at its bottom sits on the bar's bottom edge whatever
// the height of the right slot.
export const tabListContentWrapperCva = cva("flex flex-1 overflow-hidden relative", {
  variants: {
    orientation: {
      horizontal: "flex-row self-stretch",
      vertical: "flex-col w-full",
    },
    buttons: {
      true: null,
      false: null,
    },
    header: {
      true: null,
      false: null,
    },
  },
  compoundVariants: [
    {
      // Reaches 1px into the list's bottom border, so the 2px active inkbar covers the base line instead of
      // sitting above it.
      orientation: "horizontal",
      header: false,
      class: "-mb-px",
    },
    {
      orientation: "horizontal",
      buttons: true,
      class: "mx-8",
    },
    {
      orientation: "vertical",
      buttons: true,
      class: "my-8",
    },
  ],
  defaultVariants: {
    header: false,
  },
});

// The strip scrolls natively (wheel, trackpad, touch, keyboard focus) but never shows a scrollbar; the buttons and
// the wheel mapping in VTabList are the visible affordances.
export const tabListItemsContainerCva = cva("flex flex-1 relative [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", {
  variants: {
    orientation: {
      horizontal: "flex-row",
      vertical: "flex-col",
    },
    tile: {
      true: "gap-1.5",
      false: null,
    },
    divider: {
      true: null,
      false: null,
    },
    center: {
      true: "justify-center",
      false: null,
    },
    listPosition: {
      left: null,
      right: null,
    },
    scrollable: {
      true: null,
      false: null,
    },
    // The header bar's tabs sit flush against each other.
    header: {
      true: null,
      false: null,
    },
  },
  compoundVariants: [
    {
      tile: false,
      divider: false,
      header: false,
      class: "gap-1",
    },
    {
      tile: true,
      listPosition: "left",
      orientation: "vertical",
      class: "pr-0",
    },
    {
      tile: true,
      listPosition: "right",
      orientation: "vertical",
      class: "pl-0",
    },
    {
      // A horizontal strip always follows trackpad swipes, touch and the (translated) mouse wheel; without
      // `scrollable` the buttons stay as extra affordance.
      orientation: "horizontal",
      class: "overflow-x-auto overflow-y-hidden",
    },
    {
      // A vertical rail always follows the wheel and touch scrolling; without `scrollable` the buttons stay as extra affordance.
      orientation: "vertical",
      class: "overflow-y-auto overflow-x-hidden",
    },
  ],
  defaultVariants: {
    header: false,
  },
});

// Grows to at least the visible rail, so the inkbar track inside it also covers tabs scrolled into view.
export const tabListItemCva = cva("flex relative grow", {
  variants: {
    orientation: {
      horizontal: "flex-row items-center",
      vertical: "flex-col items-stretch w-full",
    },
    tile: {
      true: null,
      false: null,
    },
    center: {
      true: "justify-center",
      false: null,
    },
    // The 12px inset sits inside the inkbar's wrapper, so the 2px track runs under it to both edges.
    header: {
      true: "px-3",
      false: null,
    },
  },
  compoundVariants: [
    {
      tile: true,
      orientation: "vertical",
      class: "pt-2",
    },
  ],
  defaultVariants: {
    header: false,
  },
});

export const tabListDividerCva = cva("shrink-0", {
  variants: {
    orientation: {
      horizontal: "w-0.5 h-[calc(100%-1rem)] mx-2 bg-gray-100 dark:bg-dark-600 my-2",
      vertical: "w-[calc(100%-2rem)] h-0.5 bg-gray-100 dark:bg-dark-600 my-2 self-center",
    },
    listPosition: {
      left: null,
      right: null,
    },
  },
  compoundVariants: [
    {
      orientation: "vertical",
      listPosition: "left",
      class: "mr-2",
    },
    {
      orientation: "vertical",
      listPosition: "right",
      class: "ml-2",
    },
  ],
});
